/* @ts-nocheck */
/* global google */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GoogleMapsOverlay } from '@deck.gl/google-maps';
import { PathLayer, ColumnLayer, LineLayer } from '@deck.gl/layers';
import { RotateCwSquare, RotateCcwSquare, ArrowUpFromLine, ArrowDownFromLine, Maximize2 } from 'lucide-react';
import ALRInfoPanel from './ALRInfoPanel';

const LIBRARIES = ['geometry'];

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const mapOptions = {
  disableDefaultUI: true,
  clickableIcons: false,
  mapTypeId: 'satellite',
  mapId: '24ee09cb1783e3c39c8fcb02',
  minZoom: 10,
  maxZoom: 23,
  gestureHandling: 'greedy',
};

const parseGeometry = (geom) => {
  try {
    if (typeof geom === 'object' && geom?.type === 'LineString' && geom.coordinates) {
      return geom;
    }
    if (typeof geom === 'string') {
      if (geom.startsWith('LINESTRING')) {
        const coordsStr = geom.replace('LINESTRING(', '').replace(')', '');
        const coords = coordsStr.split(',').map(pair => {
          const [lng, lat] = pair.trim().split(' ').map(parseFloat);
          return [lng, lat];
        }).filter(coord => !isNaN(coord[0]) && !isNaN(coord[1]));
        return { type: 'LineString', coordinates: coords };
      } else if (geom.startsWith('{') || geom.startsWith('[')) {
        const parsed = JSON.parse(geom);
        if (parsed?.type === 'LineString' && parsed.coordinates) {
          return { type: 'LineString', coordinates: parsed.coordinates };
        }
      }
    }
    return null;
  } catch (err) {
    console.error('Error parsing geometry:', err);
    return null;
  }
};

const formatStationDefault = (station) => {
  if (station === null || station === undefined) return 'N/A';
  const hundreds = Math.floor(station / 100);
  const remainder = station % 100;
  return `${hundreds}+${remainder.toFixed(1).padStart(4, '0')}`;
};

// Helper function to interpolate position along geometry based on station
const interpolatePositionAtStation = (geometry, fromStation, toStation, targetStation) => {
  if (!geometry || geometry.coordinates.length === 0) {
    return null;
  }
  
  const coords = geometry.coordinates;
  
  if (coords.length === 1) {
    return coords[0];
  }
  
  const totalDistance = toStation - fromStation;
  if (totalDistance === 0) {
    return coords[0];
  }
  
  const ratio = (targetStation - fromStation) / totalDistance;
  const clampedRatio = Math.max(0, Math.min(1, ratio));
  
  const startCoord = coords[0];
  const endCoord = coords[coords.length - 1];
  
  const interpolatedLng = startCoord[0] + (endCoord[0] - startCoord[0]) * clampedRatio;
  const interpolatedLat = startCoord[1] + (endCoord[1] - startCoord[1]) * clampedRatio;
  
  return [interpolatedLng, interpolatedLat];
};

// Calculate bounds for all ALR events
const calculateBounds = (alrEvents) => {
  if (!alrEvents || alrEvents.length === 0) return null;
  
  let minLat = Infinity, maxLat = -Infinity;
  let minLng = Infinity, maxLng = -Infinity;
  
  alrEvents.forEach(alr => {
    const geom = parseGeometry(alr.geom);
    if (geom?.coordinates) {
      geom.coordinates.forEach(([lng, lat]) => {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      });
    }
  });
  
  if (minLat === Infinity) return null;
  
  return {
    north: maxLat,
    south: minLat,
    east: maxLng,
    west: minLng,
    center: {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    }
  };
};

function ALRMapView({ 
  allALREvents = [], 
  events = [], 
  formatStation = formatStationDefault,
  initialSelectedALR = null,
  onOpenModal = null, // Optional callback to open full modal view
  showInfoPanel = true,
}) {
  const [map, setMap] = useState(null);
  const [selectedALR, setSelectedALR] = useState(initialSelectedALR);
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const [hoveredEventId, setHoveredEventId] = useState(null);
  const deckOverlayRef = useRef(null);
  const mapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    version: 'beta',
  });

  // Fit map to show all ALRs on load
  const fitToAllALRs = useCallback(() => {
    if (!mapRef.current || allALREvents.length === 0) return;
    
    const bounds = calculateBounds(allALREvents);
    if (bounds) {
      const googleBounds = new google.maps.LatLngBounds(
        { lat: bounds.south, lng: bounds.west },
        { lat: bounds.north, lng: bounds.east }
      );
      mapRef.current.fitBounds(googleBounds, { padding: 50 });
    }
  }, [allALREvents]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
    mapRef.current = mapInstance;
    
    // Initial view: fit to all ALRs or center on first one
    if (allALREvents.length > 0) {
      const bounds = calculateBounds(allALREvents);
      if (bounds) {
        if (allALREvents.length === 1) {
          // Single ALR - zoom in close
          mapInstance.setCenter(bounds.center);
          mapInstance.setZoom(20);
          mapInstance.setTilt(45);
        } else {
          // Multiple ALRs - fit bounds
          const googleBounds = new google.maps.LatLngBounds(
            { lat: bounds.south, lng: bounds.west },
            { lat: bounds.north, lng: bounds.east }
          );
          mapInstance.fitBounds(googleBounds, { padding: 50 });
          mapInstance.setTilt(45);
        }
      }
    }
  }, [allALREvents]);

  const adjustMap = useCallback((mode, amount) => {
    if (!mapRef.current) return;
    switch (mode) {
      case "tilt":
        mapRef.current.setTilt(Math.max(0, Math.min(67.5, mapRef.current.getTilt() + amount)));
        break;
      case "rotate":
        mapRef.current.setHeading((mapRef.current.getHeading() + amount + 360) % 360);
        break;
    }
  }, []);

  // Color function based on magnitude
  const getALRColor = useCallback((magnitude) => {
    if (magnitude > 250) return [244, 67, 54, 180];   // Red
    if (magnitude > 200) return [255, 152, 0, 180];   // Orange
    return [255, 193, 7, 180];                        // Amber
  }, []);

  // ALR layers - matches FusionMap rendering
  const alrLayers = useMemo(() => {
    if (!allALREvents || allALREvents.length === 0) {
      return { nonSelected: [], selectedGround: [], selectedElevated: [] };
    }

    const ELEVATION_METERS = 3;
    const CURTAIN_SKIP = 2;
    const CURTAIN_WIDTH_PX = 2;

    const data = allALREvents
      .map(ev => {
        const geometry = parseGeometry(ev.geom);
        const coords = geometry?.coordinates || [];
        if (!coords.length) return null;

        const isSelected = !!(selectedALR && selectedALR.event_id === ev.event_id);
        const elevatedZ = isSelected ? ELEVATION_METERS : 0;

        const pathElevated = coords.map(([lng, lat]) => [lng, lat, elevatedZ]);
        const pathGround = coords.map(([lng, lat]) => [lng, lat, 0]);

        const curtainSegments = isSelected
          ? pathElevated
              .map((p, i) => (i % CURTAIN_SKIP === 0 || i === pathElevated.length - 1)
                ? { s: p, t: [p[0], p[1], 0] }
                : null
              )
              .filter(Boolean)
          : [];

        return {
          ...ev,
          __isSelected: isSelected,
          __pathElevated: pathElevated,
          __pathGround: pathGround,
          __curtain: curtainSegments
        };
      })
      .filter(Boolean);

    if (!data.length) return { nonSelected: [], selectedGround: [], selectedElevated: [] };

    const opaque = rgba => {
      if (!Array.isArray(rgba)) return [0, 0, 0, 255];
      const [r = 0, g = 0, b = 0] = rgba;
      return [r, g, b, 255];
    };

    const allData = data;
    const selectedData = data.filter(d => d.__isSelected);

    const nonSelectedLayers = [];
    const selectedGroundLayers = [];
    const selectedElevatedLayers = [];

    // === ALL ALRS AT GROUND LEVEL ===
    if (allData.length > 0) {
      // Ground trace shadow
      nonSelectedLayers.push(
        new PathLayer({
          id: 'alr-ground-trace-all',
          data: allData,
          getPath: d => d.__pathGround,
          getColor: [0, 0, 0, 60],
          getWidth: 2,
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          billboard: true,
          pickable: false,
          parameters: { depthTest: false }
        })
      );

      // Black casing
      nonSelectedLayers.push(
        new PathLayer({
          id: 'alr-casing-all',
          data: allData,
          getPath: d => d.__pathGround,
          getColor: [0, 0, 0, 255],
          getWidth: 4.5,
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          billboard: true,
          pickable: false,
          parameters: { depthTest: false }
        })
      );

      // Colored fill - pickable
      nonSelectedLayers.push(
        new PathLayer({
          id: 'alr-colored-all',
          data: allData,
          getPath: d => d.__pathGround,
          getColor: d => {
            const isHovered = hoveredEventId === d.event_id;
            if (isHovered) return [255, 255, 255, 255];
            return opaque(getALRColor(d.magnitude_in ?? 0));
          },
          getWidth: d => hoveredEventId === d.event_id ? 3.5 : 2.5,
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          billboard: true,
          pickable: true,
          autoHighlight: false,
          onHover: ({ object, x, y }) => {
            if (object) {
              setHoveredEventId(object.event_id);
              setHoverTooltip({
                x,
                y,
                content: {
                  type: 'ALR',
                  fromStation: formatStation(object.from_station_ft),
                  toStation: formatStation(object.to_station_ft),
                  magnitude: (object.magnitude_in || 0).toFixed(2),
                  wheelpath: object.wheelpath || 'N/A',
                }
              });
            } else {
              setHoveredEventId(null);
              setHoverTooltip(null);
            }
          },
          onClick: ({ object }) => {
            if (object) {
              setSelectedALR(object);
              setHoveredEventId(null);
              setHoverTooltip(null);
            }
          },
          parameters: { depthTest: false }
        })
      );
    }

    // === SELECTED GROUND ELEMENTS (shadow/trace) ===
    if (selectedData.length > 0) {
      selectedGroundLayers.push(
        new PathLayer({
          id: 'alr-ground-trace-selected',
          data: selectedData,
          getPath: d => d.__pathGround,
          getColor: [0, 0, 0, 255],
          opacity: 0.2,
          getWidth: 3,
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          billboard: true,
          pickable: false,
          parameters: { depthTest: false }
        })
      );

      // Curtain extrusion (vertical lines)
      selectedGroundLayers.push(
        new LineLayer({
          id: 'alr-curtain-extrusion',
          data: selectedData.flatMap(d => d.__curtain),
          getSourcePosition: d => d.s,
          getTargetPosition: d => d.t,
          getColor: [0, 0, 0, 255],
          getWidth: CURTAIN_WIDTH_PX,
          widthUnits: 'pixels',
          pickable: false,
          billboard: true,
          parameters: { depthTest: false }
        })
      );
    }

    // === SELECTED ELEVATED ELEMENTS ===
    if (selectedData.length > 0) {
      // Black casing
      selectedElevatedLayers.push(
        new PathLayer({
          id: 'alr-casing-selected',
          data: selectedData,
          getPath: d => d.__pathElevated,
          getColor: [0, 0, 0, 255],
          getWidth: 5.5,
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          billboard: true,
          pickable: false,
          parameters: { depthTest: false }
        })
      );

      // Colored fill
      selectedElevatedLayers.push(
        new PathLayer({
          id: 'alr-colored-selected',
          data: selectedData,
          getPath: d => d.__pathElevated,
          getColor: d => opaque(getALRColor(d.magnitude_in ?? 0)),
          getWidth: 3,
          widthUnits: 'pixels',
          capRounded: true,
          jointRounded: true,
          billboard: true,
          pickable: true,
          autoHighlight: true,
          highlightColor: [255, 255, 255, 140],
          onClick: ({ object }) => object && setSelectedALR(object),
          parameters: { depthTest: false }
        })
      );
    }

    return { nonSelected: nonSelectedLayers, selectedGround: selectedGroundLayers, selectedElevated: selectedElevatedLayers };
  }, [allALREvents, selectedALR, getALRColor, hoveredEventId, formatStation]);

  // Dip layers for selected ALR
  const dipLayers = useMemo(() => {
    if (!selectedALR || !events || events.length === 0) return [];

    let visibleEvents = events.filter(e => {
      const type = (e.event_type || '').toUpperCase();
      return type === 'DIP';
    });

    const alrFromStation = selectedALR.from_station_ft;
    const alrToStation = selectedALR.to_station_ft;
    const alrWheelpath = selectedALR.wheelpath;

    visibleEvents = visibleEvents.filter(event => {
      const eventFromStation = event.from_station_ft;
      const eventToStation = event.to_station_ft;
      const eventWheelpath = event.wheelpath;

      const withinStationRange = 
        (eventFromStation >= alrFromStation && eventFromStation <= alrToStation) ||
        (eventToStation >= alrFromStation && eventToStation <= alrToStation) ||
        (eventFromStation <= alrFromStation && eventToStation >= alrToStation);

      const wheelpathMatches = !alrWheelpath || eventWheelpath === alrWheelpath;

      return withinStationRange && wheelpathMatches;
    });

    if (visibleEvents.length === 0) return [];

    const ALR_ELEVATION_METERS = 3;
    const ELEVATION_SCALE = 10;
    const NUM_LEVELS = 10;
    const NUM_SEGMENTS = 48;

    const segmentData = [];
    visibleEvents.forEach(event => {
      const fromStation = event.from_station_ft;
      const toStation = event.to_station_ft;
      const peakStation = event.peak_station_ft || fromStation;
      const magnitude = event.magnitude_in || 0;
      const geometry = parseGeometry(event.geom);
      
      if (!geometry) return;
      
      for (let i = 0; i < NUM_SEGMENTS; i++) {
        const segmentRatio = i / (NUM_SEGMENTS - 1);
        const currentStation = fromStation + (toStation - fromStation) * segmentRatio;
        
        const distanceToPeak = Math.abs(currentStation - peakStation);
        
        const maxDistFromPeak = Math.max(peakStation - fromStation, toStation - peakStation);
        const heightRatio = maxDistFromPeak > 0 
          ? 1 - Math.pow(distanceToPeak / maxDistFromPeak, 2)
          : 1;
        
        const segmentHeight = magnitude * Math.max(0, heightRatio);
        
        const position = interpolatePositionAtStation(
          geometry,
          fromStation,
          toStation,
          currentStation
        );
        
        if (position) {
          segmentData.push({
            ...event,
            segmentIndex: i,
            position,
            segmentHeight,
            currentStation,
          });
        }
      }
    });

    const layers = [];

    for (let level = 0; level < NUM_LEVELS; level++) {
      const levelRatio = (level + 1) / NUM_LEVELS;
      
      layers.push(
        new ColumnLayer({
          id: `dip-pyramid-level-${level}`,
          data: segmentData,
          
          getPosition: d => {
            const [lng, lat] = d.position;
            const fullDepth = d.segmentHeight * ELEVATION_SCALE;
            const bottomZ = ALR_ELEVATION_METERS - fullDepth;
            const levelHeight = (fullDepth * level) / NUM_LEVELS;
            const baseZ = bottomZ + levelHeight;
            return [lng, lat, baseZ];
          },
          
          getElevation: d => (d.segmentHeight * ELEVATION_SCALE) / NUM_LEVELS,
          elevationScale: 1,

          getFillColor: d => {
            const baseAlpha = 255 - (level * 12);
            const isHovered = d.event_id === hoveredEventId;
            return isHovered 
              ? [255, 170, 50, Math.min(255, baseAlpha + 60)] 
              : [255, 140, 0, baseAlpha];
          },
          
          flatShading: false,
          coverage: 1.4,
          diskResolution: 32,
          radius: 0.18 * levelRatio,
          radiusUnits: 'meters',
          extruded: true,
          
          material: {
            ambient: 0.7,
            diffuse: 0.3,
            shininess: 8,
            specularColor: [15, 15, 15]
          },
          
          parameters: { depthTest: false },
          visible: true,
          opacity: 1,
          pickable: true,
          autoHighlight: false,
          
          onHover: ({ object, x, y }) => {
            if (object) {
              setHoveredEventId(object.event_id);
              setHoverTooltip({
                x,
                y,
                content: {
                  type: 'Dip',
                  fromStation: formatStation(object.from_station_ft),
                  toStation: formatStation(object.to_station_ft),
                  peakStation: formatStation(object.peak_station_ft || object.from_station_ft),
                  magnitude: (object.magnitude_in || 0).toFixed(2),
                  wheelpath: object.wheelpath || 'N/A',
                }
              });
            } else {
              setHoveredEventId(null);
              setHoverTooltip(null);
            }
          },
        })
      );
    }

    return layers;
  }, [events, selectedALR, hoveredEventId, formatStation]);

  // Bump layers for selected ALR
  const bumpLayers = useMemo(() => {
    if (!selectedALR || !events || events.length === 0) return [];

    let visibleEvents = events.filter(e => {
      const type = (e.event_type || '').toUpperCase();
      return type === 'BUMP';
    });

    const alrFromStation = selectedALR.from_station_ft;
    const alrToStation = selectedALR.to_station_ft;
    const alrWheelpath = selectedALR.wheelpath;

    visibleEvents = visibleEvents.filter(event => {
      const eventFromStation = event.from_station_ft;
      const eventToStation = event.to_station_ft;
      const eventWheelpath = event.wheelpath;

      const withinStationRange = 
        (eventFromStation >= alrFromStation && eventFromStation <= alrToStation) ||
        (eventToStation >= alrFromStation && eventToStation <= alrToStation) ||
        (eventFromStation <= alrFromStation && eventToStation >= alrToStation);

      const wheelpathMatches = !alrWheelpath || eventWheelpath === alrWheelpath;

      return withinStationRange && wheelpathMatches;
    });

    if (visibleEvents.length === 0) return [];

    const ALR_ELEVATION_METERS = 3;
    const ELEVATION_SCALE = 10;
    const NUM_LEVELS = 10;
    const NUM_SEGMENTS = 48;

    const segmentData = [];
    visibleEvents.forEach(event => {
      const fromStation = event.from_station_ft;
      const toStation = event.to_station_ft;
      const peakStation = event.peak_station_ft || fromStation;
      const magnitude = event.magnitude_in || 0;
      const geometry = parseGeometry(event.geom);
      
      if (!geometry) return;
      
      for (let i = 0; i < NUM_SEGMENTS; i++) {
        const segmentRatio = i / (NUM_SEGMENTS - 1);
        const currentStation = fromStation + (toStation - fromStation) * segmentRatio;
        
        const distanceToPeak = Math.abs(currentStation - peakStation);
        
        const maxDistFromPeak = Math.max(peakStation - fromStation, toStation - peakStation);
        const heightRatio = maxDistFromPeak > 0 
          ? 1 - Math.pow(distanceToPeak / maxDistFromPeak, 2)
          : 1;
        
        const segmentHeight = magnitude * Math.max(0, heightRatio);
        
        const position = interpolatePositionAtStation(
          geometry,
          fromStation,
          toStation,
          currentStation
        );
        
        if (position) {
          segmentData.push({
            ...event,
            segmentIndex: i,
            position,
            segmentHeight,
            currentStation,
          });
        }
      }
    });

    const layers = [];

    for (let level = 0; level < NUM_LEVELS; level++) {
      const levelRatio = (NUM_LEVELS - level) / NUM_LEVELS;
      
      layers.push(
        new ColumnLayer({
          id: `bump-pyramid-level-${level}`,
          data: segmentData,
          
          getPosition: d => {
            const [lng, lat] = d.position;
            const levelHeight = (d.segmentHeight * ELEVATION_SCALE * level) / NUM_LEVELS;
            const baseZ = ALR_ELEVATION_METERS + levelHeight;
            return [lng, lat, baseZ];
          },
          
          getElevation: d => d.segmentHeight / NUM_LEVELS,
          elevationScale: ELEVATION_SCALE,

          getFillColor: d => {
            const baseAlpha = 255 - (level * 12);
            const isHovered = d.event_id === hoveredEventId;
            return isHovered 
              ? [255, 80, 80, Math.min(255, baseAlpha + 60)] 
              : [255, 0, 0, baseAlpha];
          },
          
          flatShading: false,
          coverage: 1.4,
          diskResolution: 32,
          radius: 0.18 * levelRatio,
          radiusUnits: 'meters',
          extruded: true,
          
          material: {
            ambient: 0.7,
            diffuse: 0.3,
            shininess: 8,
            specularColor: [15, 15, 15]
          },
          
          parameters: { depthTest: false },
          visible: true,
          opacity: 1,
          pickable: true,
          autoHighlight: false,
          
          onHover: ({ object, x, y }) => {
            if (object) {
              setHoveredEventId(object.event_id);
              setHoverTooltip({
                x,
                y,
                content: {
                  type: 'Bump',
                  fromStation: formatStation(object.from_station_ft),
                  toStation: formatStation(object.to_station_ft),
                  peakStation: formatStation(object.peak_station_ft || object.from_station_ft),
                  magnitude: (object.magnitude_in || 0).toFixed(2),
                  wheelpath: object.wheelpath || 'N/A',
                }
              });
            } else {
              setHoveredEventId(null);
              setHoverTooltip(null);
            }
          },
        })
      );
    }

    return layers;
  }, [events, selectedALR, hoveredEventId, formatStation]);

  // Deck.gl overlay management
  useEffect(() => {
    if (!map) return;

    // Layer ordering matches FusionMap:
    // When ALR is selected: render ALL ALRs at ground + selected elevated
    // When no ALR selected: just render all at ground
    const allLayers = selectedALR
      ? [
          ...alrLayers.nonSelected,       // ALL ALRs at ground level
          ...alrLayers.selectedGround,    // Selected ALR shadow + curtain
          ...dipLayers,                   // Dips in front of shadow
          ...alrLayers.selectedElevated,  // Selected ALR elevated path on top
          ...bumpLayers,
        ]
      : [
          ...alrLayers.nonSelected,       // ALL ALRs at ground level
          ...dipLayers,
          ...bumpLayers,
        ];

    if (!deckOverlayRef.current) {
      const overlay = new GoogleMapsOverlay({ layers: allLayers });
      overlay.setMap(map);
      deckOverlayRef.current = overlay;
    }

    deckOverlayRef.current.setProps({ layers: allLayers });
  }, [map, alrLayers, dipLayers, bumpLayers, selectedALR]);

  // Pan to selected ALR when it changes
  useEffect(() => {
    if (map && selectedALR) {
      const geometry = parseGeometry(selectedALR.geom);
      if (geometry?.coordinates && geometry.coordinates.length > 0) {
        const coords = geometry.coordinates;
        const midIndex = Math.floor(coords.length / 2);
        const centerPoint = { lat: coords[midIndex][1], lng: coords[midIndex][0] };
        map.panTo(centerPoint);
        map.setZoom(22);
      }
    }
  }, [selectedALR, map]);

  // Update when allALREvents changes (e.g., filter applied)
  useEffect(() => {
    if (map && allALREvents.length > 0 && !selectedALR) {
      fitToAllALRs();
    }
  }, [allALREvents, map, selectedALR, fitToAllALRs]);

  if (loadError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="text-red-500 text-lg font-medium">Error loading map</div>
          <div className="text-gray-500 text-sm mt-1">Please check your API key configuration</div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="flex items-center gap-2">
          <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-gray-600">Loading Map...</span>
        </div>
      </div>
    );
  }

  if (allALREvents.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="text-gray-500 text-lg font-medium">No ALRs to display</div>
          <div className="text-gray-400 text-sm mt-1">Apply a filter or ensure ALR data is available</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        options={mapOptions}
        onLoad={onLoad}
      />
      
      {/* Hover Tooltip */}
      {hoverTooltip && (
        <div
          className="fixed bg-gray-800 text-white p-3 rounded-lg shadow-xl text-sm z-[2000] pointer-events-none"
          style={{
  left: hoverTooltip.x,
  top: hoverTooltip.y + 80,
}}
        >
          <div className="font-semibold mb-2 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              hoverTooltip.content.type === 'Bump' ? 'bg-red-500' : 
              hoverTooltip.content.type === 'Dip' ? 'bg-orange-500' : 
              'bg-yellow-500'
            }`}></div>
            {hoverTooltip.content.type}
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-gray-300">From:</span>
              <span className="font-mono">{hoverTooltip.content.fromStation}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-300">To:</span>
              <span className="font-mono">{hoverTooltip.content.toStation}</span>
            </div>
            {hoverTooltip.content.peakStation && (
              <div className="flex justify-between gap-4">
                <span className="text-gray-300">Peak:</span>
                <span className="font-mono">{hoverTooltip.content.peakStation}</span>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <span className="text-gray-300">Magnitude:</span>
              <span className={`font-mono font-semibold ${
                hoverTooltip.content.type === 'Bump' ? 'text-red-400' : 
                hoverTooltip.content.type === 'Dip' ? 'text-orange-400' : 
                'text-yellow-400'
              }`}>
                {hoverTooltip.content.magnitude} {hoverTooltip.content.type === 'ALR' ? 'in/mi' : 'in'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-300">Wheelpath:</span>
              <span className="font-mono">{hoverTooltip.content.wheelpath}</span>
            </div>
          </div>
          {hoverTooltip.content.type === 'ALR' && (
            <div className="mt-2 pt-2 border-t border-gray-600 text-xs text-gray-400">
              Click to select
            </div>
          )}
        </div>
      )}
      
      {/* Map Controls */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2 bg-white rounded-lg shadow-lg p-2 z-[1001]">
        <button
          onClick={fitToAllALRs}
          className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-all"
          title="Fit to all ALRs"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <div className="w-px h-6 bg-gray-200" />
        <button
          onClick={() => adjustMap("rotate", 45)}
          className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-all"
          title="Rotate Left"
        >
          <RotateCcwSquare className="h-4 w-4" />
        </button>
        <button
          onClick={() => adjustMap("tilt", -15)}
          className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-all"
          title="Tilt Up"
        >
          <ArrowUpFromLine className="h-4 w-4" />
        </button>
        <button
          onClick={() => adjustMap("tilt", 15)}
          className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-all"
          title="Tilt Down"
        >
          <ArrowDownFromLine className="h-4 w-4" />
        </button>
        <button
          onClick={() => adjustMap("rotate", -45)}
          className="p-2 rounded-md bg-white text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-all"
          title="Rotate Right"
        >
          <RotateCwSquare className="h-4 w-4" />
        </button>
      </div>

      {/* Info Panel for selected ALR */}
      {showInfoPanel && selectedALR && (
        <ALRInfoPanel
          event={selectedALR}
          onClose={() => setSelectedALR(null)}
          allALREvents={allALREvents}
          onSelectALR={setSelectedALR}
          events={events}
        />
      )}

      {/* Legend */}
      <div className="absolute top-4 left-4 bg-white rounded-sm border border-gray-200 shadow-lg p-3 z-[1001]">
        <div className="text-xs font-semibold text-gray-700 mb-2">ALR Severity</div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(255, 193, 7)' }}></div>
            <span className="text-gray-600">&lt; 200 in/mi</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(255, 152, 0)' }}></div>
            <span className="text-gray-600">200-250 in/mi</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'rgb(244, 67, 54)' }}></div>
            <span className="text-gray-600">&gt; 250 in/mi</span>
          </div>
        </div>
        <div className="border-t border-gray-200 mt-2 pt-2">
          <div className="text-xs text-gray-500">
            {allALREvents.length} ALR{allALREvents.length !== 1 ? 's' : ''} shown
          </div>
        </div>
      </div>
    </div>
  );
}

export default ALRMapView;